import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const MetricInfoPopover = ({
    title,
    description,
    accounts,      // Array of {accountNumber: '3010', accountName: 'Försäljning', amount: 12345}
    period,        // {startDate: '2025-01-01', endDate: '2025-12-31'}
    reportLink,    // URL to detailed report (optional)
    totalLabel     // Label for the total (e.g., "Totalt")
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);
    const buttonRef = useRef(null);

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(val || 0);
    };

    // Calculate total from accounts
    const total = accounts && accounts.length > 0
        ? accounts.reduce((sum, acc) => sum + (acc.amount || 0), 0)
        : 0;

    // Show top accounts, limit to 8
    const accountsToShow = accounts ? accounts.slice(0, 8) : [];
    const remainingCount = accounts ? accounts.length - 8 : 0;

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            {/* Info Icon Button */}
            <button
                type="button"
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.5,
                    transition: 'opacity 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = isOpen ? 1 : 0.5}
                title="Visa detaljer"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--secondary)' }}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" />
                </svg>
            </button>

            {/* Popover */}
            {isOpen && (
                <div
                    ref={popoverRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        marginTop: '8px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                        border: '1px solid var(--border, #d7dae3)',
                        padding: '16px',
                        width: '320px',
                        zIndex: 1000,
                        fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                >
                    {/* Arrow */}
                    <div style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderLeft: '1px solid var(--border, #d7dae3)',
                        borderTop: '1px solid var(--border, #d7dae3)',
                        transform: 'translateX(-50%) rotate(45deg)'
                    }} />

                    {/* Title */}
                    <div style={{
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: 'var(--heading, #333)',
                        marginBottom: '8px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid var(--border, #d7dae3)'
                    }}>
                        {title}
                    </div>

                    {/* Description */}
                    {description && (
                        <div style={{
                            fontSize: '0.8rem',
                            color: 'var(--secondary)',
                            marginBottom: '12px',
                            lineHeight: 1.5
                        }}>
                            {description}
                        </div>
                    )}

                    {/* Accounts with amounts */}
                    {accountsToShow.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: 'var(--secondary)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                marginBottom: '8px'
                            }}>
                                Kontofördelning
                            </div>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px'
                            }}>
                                {accountsToShow.map((account, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '6px 8px',
                                        backgroundColor: 'var(--body-bg, #F8F8F8)',
                                        borderRadius: '6px',
                                        border: '1px solid var(--border, #d7dae3)'
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: 'var(--heading, #333)',
                                                fontFamily: 'monospace'
                                            }}>
                                                {account.accountNumber}
                                            </div>
                                            <div style={{
                                                fontSize: '0.7rem',
                                                color: 'var(--secondary)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {account.accountName}
                                            </div>
                                        </div>
                                        <div style={{
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: 'var(--heading, #333)',
                                            marginLeft: '8px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {formatCurrency(account.amount)}
                                        </div>
                                    </div>
                                ))}
                                {remainingCount > 0 && (
                                    <div style={{
                                        fontSize: '0.7rem',
                                        color: 'var(--secondary)',
                                        textAlign: 'center',
                                        fontStyle: 'italic',
                                        padding: '4px 0'
                                    }}>
                                        +{remainingCount} fler konton
                                    </div>
                                )}
                            </div>
                            {/* Total */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '8px',
                                paddingTop: '8px',
                                borderTop: '1px solid var(--border, #d7dae3)'
                            }}>
                                <span style={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: 'var(--secondary)'
                                }}>
                                    {totalLabel || 'Totalt'}
                                </span>
                                <span style={{
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                    color: 'var(--heading, #333)'
                                }}>
                                    {formatCurrency(total)}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Period */}
                    {period && period.startDate && period.endDate && (
                        <div style={{
                            fontSize: '0.75rem',
                            color: 'var(--secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '8px'
                        }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--secondary)' }}>
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                                <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            {formatDate(period.startDate)} – {formatDate(period.endDate)}
                        </div>
                    )}

                    {/* Report Link */}
                    {reportLink && (
                        <Link
                            to={reportLink}
                            className="dashboard-action-link"
                            style={{
                                justifyContent: 'center',
                                marginTop: '8px',
                                fontSize: '0.8rem'
                            }}
                        >
                            <span>Visa rapport</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

export default MetricInfoPopover;
